/**
 * * # LifecycleWatcher

The LifecycleWatcher is a service that provides comprehensive lifecycle management and monitoring for web and native services. It addresses the complex scenario where service close events can occur before a service is fully launched.

** ## Key Features

- Service Lifecycle Tracking, monitoring service states: `launching`, `running`, `closed`
- In Native, subscribes to `com.hp.jarvis.servicerouting.serviceinstance.closed` channel
- Manages event subscriptions and unsubscriptions
- Handles events with specific `publisherId` values
- Provides automatic cleanup of processed events
- The lifecycle manager is initialized automatically when dependencies are set;
- Launch and Close a service - the lifecycle is automatically updated

**Premature Close Event Handling**:
- Listens for close events with null `publisherId` to catch premature closures
- Stores events that occur before service launch
- Automatically applies stored events when the corresponding service launches
- Prevents race conditions between service launch and close operations

 */
import { IEventService } from '../eventService';
import { internalLogger } from '../../interface/v1/clientOsLogger';
import { ServiceInstanceEvent } from '../JWeb/JWebEnums';
import * as T from './types';
import { EventServiceValueType } from '../eventService/types';
import { EventServiceFacade } from '../JWeb/facades/EventServiceFacade';
import { isNative } from '../JWeb';

/**
 * Interface for pending close events that occurred before service launch
 */
export interface PendingCloseEvent {
  eventInfo: EventServiceValueType;
  receivedAt: Date;
  eventPublisherId: string | null;
  timeoutId?: NodeJS.Timeout;
}

/**
 * LifecycleWatcher handles the lifecycle of web services including:
 * - Tracking service state (launching, running, closed)
 * - Managing premature close events that occur before service launch
 * - Ensuring proper cleanup and event handling
 */
export class LifecycleWatcher {
  private _eventService: IEventService;
  private _webServiceRouting: T.WebServiceRoutingType;
  private _serviceLifecycles: Map<string, T.ServiceLifecycleInfo> = new Map();
  private _pendingCloseEvents: Map<string, PendingCloseEvent[]> = new Map();
  private _nullPublisherSubscription: (() => void) | null = null;
  private _isListeningToNullPublisher = false;
  private readonly PENDING_EVENT_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes

  constructor(
    eventService: IEventService,
    webServiceRouting: T.WebServiceRoutingType
  ) {
    this._eventService = eventService;
    this._webServiceRouting = webServiceRouting;
    // Only initialize null publisher listener in native environment
    void this._initializeNullPublisherListener();
  }

  /**
   * Initialize listener for events with null publisherId to catch premature close events
   */
  private async _initializeNullPublisherListener(): Promise<void> {
    if (!(await isNative())) {
      internalLogger.debug(
        'LifecycleWatcher: Skipping null publisher listener initialization - not in native environment'
      );
      return;
    }
    if (this._isListeningToNullPublisher) return;

    try {
      const subscribeResponse = await this._eventService.subscribe(
        ServiceInstanceEvent.jarvisEventServiceInstanceClosed,
        (eventInfo) => {
          this._handlePrematureCloseEvent(eventInfo);
        },
        { publisherId: null }
      );

      this._nullPublisherSubscription = subscribeResponse.unsubscribe;
      this._isListeningToNullPublisher = true;

      internalLogger.log?.(
        'LifecycleWatcher: Successfully subscribed to null publisher events'
      );
    } catch (error) {
      internalLogger.error?.(
        'LifecycleWatcher: Failed to initialize null publisher listener',
        error
      );
    }
  }

  /**
   * Handle premature close events that occur before service launch.
   * We are already listening to ServiceInstanceEvent.jarvisEventServiceInstanceClosed channel.
   */
  private _handlePrematureCloseEvent(eventInfo: EventServiceValueType): void {
    const eventPublisherId =
      eventInfo.publisherId || eventInfo.eventData?.publisherId || null;

    // Before assuming it's a premature event, we need to check if the service is already launched with this publisherId
    const isServiceLaunched = Array.from(this._serviceLifecycles.values()).some(
      (lifecycle) =>
        (lifecycle.eventPublisherId || lifecycle.instanceId) ===
        eventPublisherId
    );
    if (isServiceLaunched) {
      internalLogger.debug(
        'LifecycleWatcher: Ignoring close event for already launched service with publisherId: ' +
          eventPublisherId
      );
      return;
    }

    internalLogger.log('LifecycleWatcher: Received premature close event', {
      eventInfo,
      eventPublisherId
    });

    // Store the premature close event
    const pendingEvent: PendingCloseEvent = {
      eventInfo: eventInfo,
      receivedAt: new Date(),
      eventPublisherId
    };

    // Create timeout for this specific event
    const timeoutId = setTimeout(() => {
      this._removeExpiredPendingEvent(pendingEvent, eventPublisherId || 'null');
    }, this.PENDING_EVENT_TIMEOUT_MS);

    pendingEvent.timeoutId = timeoutId;

    // If we don't have a specific publisherId, we'll need to match this later
    const key = eventPublisherId || 'null';
    const existing = this._pendingCloseEvents.get(key) || [];
    existing.push(pendingEvent);
    this._pendingCloseEvents.set(key, existing);

    internalLogger.log?.('LifecycleWatcher: Stored premature close event', {
      key,
      pendingEventsCount: existing.length,
      timeoutMs: this.PENDING_EVENT_TIMEOUT_MS
    });
  }

  /**
   * Remove an expired pending event and log error message
   */
  private _removeExpiredPendingEvent(
    expiredEvent: PendingCloseEvent,
    key: string
  ): void {
    const pendingEvents = this._pendingCloseEvents.get(key);
    if (!pendingEvents) return;

    const eventIndex = pendingEvents.findIndex(
      (event) =>
        event.receivedAt.getTime() === expiredEvent.receivedAt.getTime() &&
        event.eventPublisherId === expiredEvent.eventPublisherId
    );

    if (eventIndex !== -1) {
      const removedEvent = pendingEvents.splice(eventIndex, 1)[0];

      // Clear the timeout if it exists
      if (removedEvent.timeoutId) {
        clearTimeout(removedEvent.timeoutId);
      }

      // Clean up empty arrays
      if (pendingEvents.length === 0) {
        this._pendingCloseEvents.delete(key);
      } else {
        this._pendingCloseEvents.set(key, pendingEvents);
      }

      const errorMessage = `LifecycleWatcher: Removed expired pending close event after 3 minutes timeout`;
      const errorDetails = {
        key,
        eventPublisherId: expiredEvent.eventPublisherId,
        receivedAt: expiredEvent.receivedAt,
        expiredAt: new Date(),
        eventData: expiredEvent.eventInfo.eventData
      };

      internalLogger.error?.(errorMessage, errorDetails);
    }
  }

  /**
   * Register a service launch and check for pending close events
   */
  public registerServiceLaunch(serviceInstance: T.ServiceInstance): void {
    const { eventPublisherId, instanceId, serviceId } = serviceInstance;

    // Create lifecycle info
    const lifecycleInfo: T.ServiceLifecycleInfo = {
      ...serviceInstance,
      launchedAt: new Date(),
      hasReceivedMandatoryMessages: false
    };

    this._serviceLifecycles.set(instanceId, lifecycleInfo);

    internalLogger.debug('LifecycleWatcher: Service launch registered', {
      serviceId,
      instanceId,
      eventPublisherId
    });

    // Check for pending close events
    this._checkAndApplyPendingCloseEvents(lifecycleInfo);
  }

  /**
   * Below we show an example of event that has been published before the service was launched.
   * This event should be stored and applied when the service is launched.
   *
   * 20250820:17:26:54.271	- ( ----> )	Ui|EventHub:Publish|TID:1|	Event Has Been Published: (eventName: com.hp.jarvis.servicerouting.serviceinstance.closed, publisherId: com.hp.jarvis.service.routing.service.instance.2a0a8c47-daed-4585-afb9-2da1b7b92c75, eventData: {"serviceInstanceId":"2a0a8c47-daed-4585-afb9-2da1b7b92c75","reason":"SUCCESS","extraData":{"result":"ineligible","output":null,"errorInfo":null,"xCorrelationId":null}}
   */
  /**
   * Check for pending close events and apply them if found
   */
  private _checkAndApplyPendingCloseEvents(
    lifecycleInfo: T.ServiceLifecycleInfo
  ): void {
    const { eventPublisherId, instanceId } = lifecycleInfo;
    internalLogger.debug(
      'LifecycleWatcher: Checking for pending close events for eventPublisherId: ',
      eventPublisherId,
      'and instanceId: ',
      instanceId
    );

    const secondaryPublisherId =
      'com.hp.jarvis.service.routing.service.instance.' + instanceId;
    // Check for events with matching patterns, in priority order
    const keysToCheck = [
      instanceId, // When the service type=Web sends the register event
      secondaryPublisherId,
      eventPublisherId
      //'null' // Removing this to avoid false positives
    ];

    for (const key of keysToCheck) {
      if (!key) continue;

      // Here we will find an event for the received PublishId and the proper
      // channel closed. For now, it's not need a second validation using the internal data.
      const pendingEvents = this._pendingCloseEvents.get(key as string);

      // If were a match, we can apply the event. If not, we should ignore it.
      if (pendingEvents && pendingEvents.length > 0) {
        // Take the first pending event (FIFO)
        const pendingEvent = pendingEvents.shift()!;

        // Clear the timeout since the event is being processed
        if (pendingEvent.timeoutId) {
          clearTimeout(pendingEvent.timeoutId);
        }

        internalLogger.log?.(
          'LifecycleWatcher: Found pending close event for service',
          {
            instanceId,
            eventPublisherId,
            pendingEvent
          }
        );

        lifecycleInfo.prematureCloseEvent = {
          eventInfo: pendingEvent.eventInfo,
          receivedAt: pendingEvent.receivedAt
        };

        // Clean up empty arrays
        if (pendingEvents.length === 0) {
          this._pendingCloseEvents.delete(key as string);
        } else {
          this._pendingCloseEvents.set(key as string, pendingEvents);
        }

        // Trigger the close event immediately
        void this._triggerDeferredCloseEvent(
          lifecycleInfo,
          pendingEvent.eventInfo
        );
        break;
      }
    }
  }

  /**
   * Trigger a deferred close event that was received before service launch
   */
  private async _triggerDeferredCloseEvent(
    lifecycleInfo: T.ServiceLifecycleInfo,
    eventInfo: EventServiceValueType
  ): Promise<void> {
    if (!lifecycleInfo.prematureCloseEvent) return;

    const publisherId =
      lifecycleInfo.eventPublisherId || lifecycleInfo.instanceId; // Fallback to instanceId if publisherId was not set

    internalLogger.debug(
      'LifecycleWatcher: Triggering deferred close event to publisherId: ' +
        publisherId
    );

    // Emit close event through the JWeb EventService
    // I need to MIMIC what happens when a native service completes its execution and send a close event
    // This ensures that all standard close handling logic is executed
    // const publisher = await EventServiceFacade.createPublisher(publisherId);

    // This event represents the native service sending a "closed" status back to the web layer
    /*await this._eventService.publish(
      ServiceInstanceEvent.jarvisEventServiceInstanceClosed,
      eventInfo
    );*/

    const resultData: T.CloseServiceInstanceOptions['resultData'] =
      eventInfo?.eventData?.extraData;
    this._webServiceRouting.closeServiceInstance({
      resultData
    });
  }

  /**
   * Update service state to running
   */
  public updateServiceToRunning(instanceId: string): void {
    const lifecycleInfo = this._serviceLifecycles.get(instanceId);
    if (lifecycleInfo) {
      lifecycleInfo.state = 'running';
      lifecycleInfo.hasReceivedMandatoryMessages = true;

      internalLogger.log?.('LifecycleWatcher: Service updated to running', {
        instanceId,
        serviceId: lifecycleInfo.serviceId
      });
    }
  }

  /**
   * Register service closure
   */
  public registerServiceClosure(
    instanceId: string,
    closeOptions?: T.CloseServiceInstanceOptions
  ): void {
    const lifecycleInfo = this._serviceLifecycles.get(instanceId);
    if (lifecycleInfo) {
      lifecycleInfo.state = 'closed';
      lifecycleInfo.closedAt = new Date();

      internalLogger.log?.('LifecycleWatcher: Service closure registered', {
        instanceId,
        serviceId: lifecycleInfo.serviceId,
        closeOptions
      });
    }
  }

  /**
   * Get lifecycle information for a service instance
   */
  public getServiceLifecycle(
    instanceId: string
  ): T.ServiceLifecycleInfo | undefined {
    return this._serviceLifecycles.get(instanceId);
  }

  /**
   * Get all service lifecycles
   */
  public getAllServiceLifecycles(): Map<string, T.ServiceLifecycleInfo> {
    return new Map(this._serviceLifecycles);
  }

  /**
   * Get pending close events
   */
  public getPendingCloseEvents(): Map<string, PendingCloseEvent[]> {
    return new Map(this._pendingCloseEvents);
  }

  /**
   * Clean up old service lifecycle records
   */
  public cleanup(olderThanMinutes: number = 60): void {
    const cutoffTime = new Date(Date.now() - olderThanMinutes * 60 * 1000);

    // Clean up old service lifecycles
    const serviceKeys = Array.from(this._serviceLifecycles.keys());
    for (const instanceId of serviceKeys) {
      const lifecycleInfo = this._serviceLifecycles.get(instanceId);
      if (lifecycleInfo?.closedAt && lifecycleInfo.closedAt < cutoffTime) {
        this._serviceLifecycles.delete(instanceId);
      }
    }

    // Also clean up old pending events
    const pendingKeys = Array.from(this._pendingCloseEvents.keys());
    for (const key of pendingKeys) {
      const pendingEvents = this._pendingCloseEvents.get(key);
      if (pendingEvents) {
        const expiredEvents = pendingEvents.filter(
          (event) => event.receivedAt < cutoffTime
        );
        const validEvents = pendingEvents.filter(
          (event) => event.receivedAt >= cutoffTime
        );

        // Clear timeouts for expired events
        for (const expiredEvent of expiredEvents) {
          if (expiredEvent.timeoutId) {
            clearTimeout(expiredEvent.timeoutId);
          }
        }

        if (validEvents.length === 0) {
          this._pendingCloseEvents.delete(key);
        } else {
          this._pendingCloseEvents.set(key, validEvents);
        }
      }
    }

    internalLogger.log('LifecycleWatcher: Cleanup completed', {
      cutoffTime,
      remainingServices: this._serviceLifecycles.size,
      remainingPendingEvents: Array.from(
        this._pendingCloseEvents.values()
      ).reduce((sum, arr) => sum + arr.length, 0)
    });
  }

  /**
   * Dispose and clean up all resources
   */
  public dispose(): void {
    // Clear all pending event timeouts
    const pendingEventsEntries = Array.from(this._pendingCloseEvents.values());
    for (const pendingEvents of pendingEventsEntries) {
      for (const event of pendingEvents) {
        if (event.timeoutId) {
          clearTimeout(event.timeoutId);
        }
      }
    }

    // Unsubscribe from null publisher events
    if (this._nullPublisherSubscription) {
      try {
        this._nullPublisherSubscription();
        this._nullPublisherSubscription = null;
        this._isListeningToNullPublisher = false;
      } catch (error) {
        internalLogger.error?.(
          'LifecycleWatcher: Error unsubscribing from null publisher',
          error
        );
      }
    }

    // Clear all data
    this._serviceLifecycles.clear();
    this._pendingCloseEvents.clear();

    internalLogger.log('LifecycleWatcher: Disposed successfully');
  }
}

export default LifecycleWatcher;
